import {
  CpCellType,
  GeoSplitType,
  GeoTestMethod,
  GeoTestType,
  IGeoTest,
  IRecommendedTest,
  ITestMetadata,
  ITestMetrics,
  Status,
  VendorLevel
} from '@libs/apis';
import {
  GeoCellDisplayStatus,
  ICPAdTestConfig
} from '@portal/app/geo-designer/models/ICPAdTestConfig';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import { GeoDesignerConstants } from '@portal/app/geo-designer/common/geo-designer.constants';
import { GeoTestBudgetLevel } from '@portal/app/geo-designer/common/geo-tests-field-definition';
import { uniqBy } from 'lodash-es';

export class GeoTestDetails {
  public readonly recommendedTest: IRecommendedTest | null = null;
  public readonly geoTestObject: IGeoTest | null = null;
  public readonly geoStudy: ICPAdTestConfig | null = null;

  public get testName(): string | null {
    if (this.geoStudy?.cellConfigs[0]) {
      return `${this.geoStudy.cellConfigs[0].cellName}`;
    } else if (this.recommendedTest) {
      return this.recommendedTest.name;
    }
    return null;
  }

  public get testRecommendation(): IRecommendedTest | null {
    return this.recommendedTest || this.geoTestObject?.recommendedTest || null;
  }

  public get tactics(): string[] {
    if (this.geoStudy?.cellConfigs[0]) {
      return uniqBy(this.geoStudy.cellConfigs[0].taxonomyFilters, 'tactic').map(
        (taxonomy) => taxonomy.tactic
      );
    } else if (this.recommendedTest && this.recommendedTest.tactics?.length) {
      return this.recommendedTest.tactics.map((tacticMap) => tacticMap.tactic);
    } else if (this.recommendedTest && this.recommendedTest.tactic) {
      return [this.recommendedTest.tactic];
    }
    return [];
  }

  public get channels(): string[] {
    if (this.geoStudy?.cellConfigs[0]) {
      return this.geoStudy.cellConfigs[0].taxonomyFilters.map(
        (taxonomy) => taxonomy.channel
      );
    } else if (this.recommendedTest) {
      return (this.recommendedTest.channel || '').split(', ');
    }
    return [];
  }

  public get type(): GeoTestType {
    if (this.geoStudy?.cellConfigs[0]) {
      return this.geoStudy.cellConfigs[0].isContributionOnly
        ? GeoTestType.lift
        : this.geoStudy.cellConfigs[0].cellType === CpCellType.CONTROL_SCALE
          ? GeoTestType.scale
          : GeoTestType.holdout;
    }
    return this.recommendedTest?.testType || GeoTestType.holdout;
  }

  public set type(testType: GeoTestType) {
    if (this.geoTestObject) {
      this.geoTestObject.metadata.testType = testType;
    }
    if (this.recommendedTest) {
      this.recommendedTest.testType = testType;
    }
  }

  public get compositeDataSourceId(): number | null {
    if (this.geoStudy?.cellConfigs[0]) {
      return this.geoStudy.cellConfigs[0].compositeDataSourceId || null;
    }
    return this.recommendedTest?.compositeDataSourceId || null;
  }

  public get geoSplitLevel(): GeoSplitType | null {
    if (this.geoStudy) {
      return this.geoStudy.geoSplitLevel as GeoSplitType;
    }
    return this.recommendedTest ? GeoSplitType.REGION : null;
  }

  public get budgetLevel(): GeoTestBudgetLevel {
    if (this.geoStudy?.cellConfigs[0]) {
      return this.geoStudy.cellConfigs[0].platformInfo?.level ===
        VendorLevel.SUB_CAMPAIGN
        ? GeoTestBudgetLevel.adset
        : GeoTestBudgetLevel.campaign;
    }
    return this.recommendedTest?.platformInfo?.level ===
      VendorLevel.SUB_CAMPAIGN
      ? GeoTestBudgetLevel.adset
      : GeoTestBudgetLevel.campaign;
  }

  public get disableSync(): boolean {
    return this.geoStudy
      ? Boolean(this.geoStudy.cellConfigs[0]?.disableSync)
      : !this.recommendedTest ||
          this.recommendedTest.method === GeoTestMethod.manual;
  }

  public get method(): GeoTestMethod {
    if (this.geoTestObject) {
      return this.geoTestObject.metadata.method;
    }
    if (this.recommendedTest) return this.recommendedTest.method;
    else return GeoTestMethod.manual;
  }

  public set method(method: GeoTestMethod) {
    if (this.geoTestObject) {
      this.geoTestObject.metadata.method = method;
    }
    if (this.recommendedTest) {
      this.recommendedTest.method = method;
    }
  }

  public get status(): GeoCellDisplayStatus {
    if (this.geoStudy) {
      return this.geoStudy.displayStatus;
    }
    return GeoCellDisplayStatus.none;
  }

  public get dbStatus(): Status {
    if (this.geoStudy) {
      return this.geoStudy.status as Status;
    }
    return Status.DRAFT;
  }

  public get metrics(): ITestMetrics | null {
    if (this.geoTestObject) {
      const metrics = this.geoTestObject.metrics;
      return {
        mediaSpend: metrics.mediaSpend > 0 ? metrics.mediaSpend : 0,
        percOrdersI: metrics.percOrdersI || 0,
        percInc: metrics.percInc || 0,
        additionalSpend: 0,
        percSalesContribution: 0,
        refMediaSpend: this.geoTestObject.metrics.refMediaSpend,
        refPercOrdersI: this.geoTestObject.metrics.refPercOrdersI
      };
    }
    if (this.recommendedTest) {
      return this.recommendedTest.metrics;
    }
    return null;
  }

  public get referenceMetrics(): Partial<ITestMetrics> {
    if (this.geoTestObject) {
      const cellConfig = this.geoTestObject.geoStudy.cellConfigs[0];
      return {
        mediaSpend: cellConfig?.mediaSpend,
        percOrdersI: cellConfig?.percOrdersI,
        percInc: 0,
        additionalSpend: 0,
        percSalesContribution: 0,
        refMediaSpend: this.geoTestObject.metrics.refMediaSpend,
        refPercOrdersI: this.geoTestObject.metrics.refPercOrdersI
      };
    }
    if (this.recommendedTest) {
      return this.recommendedTest.metrics;
    }
    return {};
  }

  public get metadata(): ITestMetadata | null {
    if (this.geoTestObject) {
      return this.geoTestObject.metadata;
    }
    if (this.testRecommendation) {
      return this.testRecommendation.metadata;
    }
    return null;
  }

  public get isTestActive(): boolean {
    return this.status === GeoCellDisplayStatus.inProgress;
  }

  public get isTestScheduled(): boolean {
    return (
      this.status === GeoCellDisplayStatus.scheduled ||
      this.status === GeoCellDisplayStatus.actionNeeded ||
      this.status === GeoCellDisplayStatus.draft
    );
  }

  public get isTestCompleted(): boolean {
    return this.status === GeoCellDisplayStatus.finished;
  }

  public get hasEnded(): boolean {
    return (
      !!this.geoStudy &&
      DateTimeService.isBeforeToday(new Date(this.geoStudy.endDate))
    );
  }

  public get conversionTypeId(): number {
    return (
      (this.geoStudy && this.geoStudy.conversionTypeId) ||
      (this.recommendedTest && this.recommendedTest.conversionTypeId) ||
      0
    );
  }

  public get durationInWeeks(): number {
    return (
      (this.geoStudy &&
        Math.floor(
          DateTimeService.dayDuration(
            this.geoStudy.endDate,
            this.geoStudy.startDate
          ) / GeoDesignerConstants.daysInWeek
        )) ||
      (this.recommendedTest && this.recommendedTest.durationInWeeks) ||
      0
    );
  }

  public get durationInDays(): number {
    return (
      (this.geoStudy &&
        DateTimeService.dayDuration(
          this.geoStudy.endDate,
          this.geoStudy.startDate
        )) ||
      (this.recommendedTest &&
        this.recommendedTest.durationInWeeks * GeoDesignerConstants.daysInWeek +
          GeoDesignerConstants.geoTestDurationPaddingDays) ||
      0
    );
  }

  public get studyId(): number {
    return (this.geoStudy && this.geoStudy.studyId) || 0;
  }

  public get cellConfigId(): number {
    return (this.geoStudy && this.geoStudy.cellConfigs[0]?.id) || 0;
  }

  public get scaleCost(): number {
    if (this.geoStudy) {
      if (
        this.geoStudy.cellConfigs[0]?.cellType === CpCellType.CONTROL_HOLDOUT
      ) {
        return 0;
      }
      const totalDailyBudget = this.geoStudy.cellConfigs[0]?.taxonomyFilters
        .map((taxonomyFilter) => taxonomyFilter.scale || 0)
        .reduce((budget1, budget2) => budget1 + budget2, 0);
      const durationInDays = this.durationInDays;
      if (durationInDays && totalDailyBudget) {
        return totalDailyBudget * durationInDays;
      } else {
        return this.recommendedAdditionSpend();
      }
    } else {
      return this.recommendedAdditionSpend();
    }
  }

  public set scaleCost(scaleCost: number) {
    if (this.recommendedTest) {
      this.recommendedTest.metrics.additionalSpend = scaleCost;
    }
  }

  public get isV2(): boolean {
    return (
      !!this.geoStudy &&
      (this.geoStudy.designerVersion ===
        GeoDesignerConstants.designerVersion.v2 ||
        this.geoStudy.isStudyWithVendorRemoval)
    );
  }

  public get isContributionOnly(): boolean {
    return (
      (!!this.geoStudy && !!this.geoStudy.cellConfigs[0]?.isContributionOnly) ||
      (!this.geoStudy && this.recommendedTest?.testType === GeoTestType.lift)
    );
  }

  private recommendedAdditionSpend() {
    return this.recommendedTest?.metrics.additionalSpend || 0;
  }

  constructor(
    recommendedTest: IRecommendedTest | null,
    geoTest: IGeoTest | null
  ) {
    this.recommendedTest = recommendedTest;
    this.geoTestObject = geoTest || null;
    this.geoStudy = (geoTest?.geoStudy as ICPAdTestConfig) || null;
  }
}
